import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import get from 'lodash/get';

import * as Fields from 'components/Fields';
import { Mappers } from 'modules/process';
import { http } from 'services';
import { useMutation, useQuery } from 'react-query';

interface IProps {
  name: string;
  label: string;
  registerId: number;
  required?: boolean;
  accept?: string;
  acceptAll?: boolean;
  maxSize?: number;
}

const RegisterFileUploader: React.FC<IProps> = ({ name, label, registerId, maxSize = 10, ...props }) => {
  const { values, setFieldValue, isSubmitting } = useFormikContext();
  const value = get(values, `${name}`) || 0;

  const [file, setFile] = useState({
    id: 0,
    name: '',
    extension: '',
    size: 0
  });

  const { isLoading } = useQuery({
    queryKey: ['file', 'info', registerId, value],
    queryFn: async () => {
      const { data } = await http.request.get<any>(`/file/register/info/${value}`, {
        params: {
          register_id: registerId
        }
      });

      return data;
    },
    enabled: !!registerId && !!value,
    retry: false,
    onSuccess: data => {
      const file = Mappers.file(get(data, 'data'));
      setFile(file);
    }
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await http.request.post(`/file/register/upload`, formData, {
        params: {
          register_id: registerId
        }
      });
      return data;
    }
  });

  const onSelect = (file: File) => {
    uploadMutation.mutate(file, {
      onSuccess: data => {
        const file = Mappers.file(get(data, 'data'));
        setFile(file);
        setFieldValue(name, String(file.id));
      }
    });
  };

  const downloadMutation = useMutation({
    mutationFn: async (id: number) => {
      const { data } = await http.request.get(`/file/register/download/${id}`, {
        responseType: 'blob',
        params: {
          register_id: registerId
        }
      });
      return data;
    }
  });

  const onView = (id: number) => {
    downloadMutation.mutate(id, {
      onSuccess: data => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    });
  };

  return (
    <Fields.File
      {...{ name, label, isLoading: isLoading || uploadMutation.isLoading, onSelect, onView, file, maxSize }}
      {...props}
      disabled={isSubmitting}
    />
  );
};

export default RegisterFileUploader;
